import React from "react";
import { FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";
import { ResultsMobileCard, ResultsMobileList } from "./ResultsMobile";
import "../assets/css/WebsiteLeads.css";
import "../assets/css/Circles.css";

/**
 * The shared inbox layout (status tabs with counts, search, table; stacked cards on phones), used by the
 * Digix Leads inbox, Circles admin lists and the client portal Digix Leads inbox.
 *
 * - tabs: [{ id, label, tone }] where tone is a CSS colour for the tab underline; counts: { [id]: n }.
 * - columns: [{ key, label, render(row), className }]; mobileCard(row) returns ResultsMobileCard props.
 */
export default function InboxView({
  tabs = [],
  counts = {},
  tab,
  onTab,
  search,
  onSearch,
  searchPlaceholder = "Search",
  onRefresh,
  toolbarExtra,
  notice,
  onDismissNotice,
  error,
  loading,
  rows = [],
  rowKey,
  columns = [],
  mobileCard,
  empty = "Nothing here yet.",
}) {
  const total = Object.values(counts).reduce((t, n) => t + (Number(n) || 0), 0);
  return (
    <div className="wl-panel">
      <div className="wl-toolbar">
        {tabs.length > 0 ? (
          <div className="wl-tabs" role="tablist" aria-label="Status">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`wl-tab${tab === t.id ? " is-active" : ""}`}
                style={t.tone ? { "--wl-tone": t.tone } : undefined}
                onClick={() => onTab && onTab(t.id)}
              >
                <span className="wl-tab-label">{t.label}</span>
                <span className="wl-tab-count">{t.id === "" ? total : counts[t.id] || 0}</span>
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <div className="wl-toolbar-right">
          {toolbarExtra}
          {onSearch ? (
            <label className="wl-search">
              <FaSearch aria-hidden />
              <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
            </label>
          ) : null}
          {onRefresh ? (
            <button type="button" className="wl-icon-btn" onClick={onRefresh} title="Refresh" aria-label="Refresh">
              <FaSyncAlt />
            </button>
          ) : null}
        </div>
      </div>

      {notice ? (
        <div className={`wl-alert wl-alert--${notice.type || "success"}`}>
          <span>{notice.text}</span>
          {onDismissNotice ? (
            <button type="button" className="wl-icon-btn" onClick={onDismissNotice} aria-label="Dismiss">
              <FaTimes />
            </button>
          ) : null}
        </div>
      ) : null}
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}

      <div className="wl-table-wrap co-desktop-table">
        <table className="wl-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.className}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="wl-empty">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="wl-empty">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={rowKey(row)}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.className}>
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {mobileCard ? (
        <ResultsMobileList className="wl-mobile-results" loading={loading} empty={empty}>
          {rows.map((row) => (
            <ResultsMobileCard key={rowKey(row)} {...mobileCard(row)} />
          ))}
        </ResultsMobileList>
      ) : null}
    </div>
  );
}
